import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { isUser } from '../../features/auth/authSlice';
import api from '../../utils/axiosConfig';
import {
    CheckCircleIcon,
    EyeIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    ExclamationTriangleIcon,
    ClipboardDocumentListIcon,
    MapPinIcon,
    XMarkIcon,
    LinkIcon,
    ClipboardIcon
} from '@heroicons/react/24/outline';

interface TaskApplication {
    _id: string;
    task: {
        _id: string;
        title: string;
        description: string;
        company: {
            _id: string;
            companyName: string;
            logo?: string;
        };
        regions: string[];
        states: string[];
        districts: string[];
        requiredSkills: Array<{
            _id: string;
            name: string;
        }>;
        status: string;
        startDate: string;
        endDate: string;
        createdAt: string;
    };
    status: string;
    appliedAt: string;
    reviewedAt?: string;
    assignedDesignation?: string;
    applicationMessage?: string;
    rejectionReason?: string;
}

interface AssignedTask {
    _id: string;
    title: string;
    description: string;
    taskUrl?: string;
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    regions: string[];
    states: string[];
    districts: string[];
    requiredSkills: Array<{
        _id: string;
        name: string;
    }>;
    status: string;
    startDate: string;
    endDate: string;
    createdAt: string;
    assignedUsers: Array<{
        user: string;
        designation: string;
        assignedAt: string;
        status: string;
        referralId?: string;
        referralLink?: string;
        myReferralLink?: string;
    }>;
}

interface AvailableTask {
    _id: string;
    title: string;
    description: string;
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    regions: string[];
    states: string[];
    districts: string[];
    requiredSkills: Array<{
        _id: string;
        name: string;
    }>;
    status: string;
    startDate: string;
    endDate: string;
    createdAt: string;
}

const TaskManagement: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [myApplications, setMyApplications] = useState<TaskApplication[]>([]);
    const [assignedTasks, setAssignedTasks] = useState<AssignedTask[]>([]);
    const [availableTasks, setAvailableTasks] = useState<AvailableTask[]>([]);
    const [activeTab, setActiveTab] = useState<'assigned' | 'applications' | 'available'>('assigned');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedApplication, setSelectedApplication] = useState<TaskApplication | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showApplicationForm, setShowApplicationForm] = useState(false);
    const [selectedTask, setSelectedTask] = useState<AvailableTask | null>(null);
    const [applicationMessage, setApplicationMessage] = useState('');
    const [showInvitedApplicationForm, setShowInvitedApplicationForm] = useState(false);
    const [selectedInvitedTask, setSelectedInvitedTask] = useState<any>(null);
    const [invitedApplicationMessage, setInvitedApplicationMessage] = useState('');
    const [copiedLink, setCopiedLink] = useState<string | null>(null);

    const copyToClipboard = async (text: string, linkId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedLink(linkId);
            setTimeout(() => setCopiedLink(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const fetchUserTasks = useCallback(async () => {
        try {
            const [applicationsResponse, assignedTasksResponse, availableTasksResponse] = await Promise.all([
                api.get('/task-applications/my-applications'),
                api.get('/tasks/user-tasks'),
                api.get('/tasks')
            ]);

            if (applicationsResponse.data.success) {
                setMyApplications(applicationsResponse.data.data.applications || []);
            }

            if (assignedTasksResponse.data.success) {
                setAssignedTasks(assignedTasksResponse.data.data.tasks || []);
            }

            if (availableTasksResponse.data.success) {
                setAvailableTasks(availableTasksResponse.data.data.tasks || []);
            }

            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch tasks');
            console.error('Error fetching tasks:', err);
        }
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchUserTasks();
        setRefreshing(false);
    };

    useEffect(() => {
        if (user && isUser(user)) {
            setLoading(true);
            fetchUserTasks().finally(() => setLoading(false));
        }
    }, [user, fetchUserTasks]);

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'invited':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'applied':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'withdrawn':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'paused':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const handleApplyToTask = async () => {
        if (!selectedTask) return;

        try {
            const response = await api.post(`/task-applications/apply/${selectedTask._id}`, {
                applicationMessage: applicationMessage.trim() || undefined
            });

            if (response.data.success) {
                setShowApplicationForm(false);
                setSelectedTask(null);
                setApplicationMessage('');
                await fetchUserTasks();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to apply to task');
        }
    };

    const handleApplyToInvitedTask = async () => {
        if (!selectedInvitedTask) return;

        try {
            const response = await api.post(`/task-applications/apply/${selectedInvitedTask._id}`, {
                applicationMessage: invitedApplicationMessage.trim() || 'Applied to invited task'
            });

            if (response.data.success) {
                setError(null);
                setShowInvitedApplicationForm(false);
                setSelectedInvitedTask(null);
                setInvitedApplicationMessage('');
                await fetchUserTasks();
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to submit application');
        }
    };

    const openInvitedApplicationForm = (task: any) => {
        setSelectedInvitedTask(task);
        setShowInvitedApplicationForm(true);
    };

    const openApplicationForm = (task: AvailableTask) => {
        setSelectedTask(task);
        setShowApplicationForm(true);
    };

    const closeApplicationForm = () => {
        setShowApplicationForm(false);
        setSelectedTask(null);
        setApplicationMessage('');
    };


    const closeModal = () => {
        setShowModal(false);
        setSelectedApplication(null);
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'Not specified';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Error</h3>
                <p className="text-red-600 dark:text-red-400 mt-2">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 container mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            My Tasks
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            View your assigned tasks and task applications.
                        </p>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {refreshing ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Refreshing...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Refresh
                            </>
                        )}
                    </button>
                </div>

                {/* Quick Stats */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center">
                            <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800 dark:text-green-200">My Applications</p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{myApplications.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center">
                            <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Available Tasks</p>
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{availableTasks.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center">
                            <CurrencyDollarIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Approved Tasks</p>
                                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                    {myApplications.filter(app => app.status === 'approved').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">Pending Review</p>
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                    {myApplications.filter(app => app.status === 'applied').length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8 px-6">
                        <button
                            onClick={() => setActiveTab('assigned')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'assigned'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            My Assigned Tasks ({assignedTasks.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('applications')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'applications'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            My Applications ({myApplications.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('available')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'available'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            Available Tasks ({availableTasks.length})
                        </button>
                    </nav>
                </div>

                <div className="p-6">
                    {activeTab === 'assigned' ? (
                        <>
                            {assignedTasks.length === 0 ? (
                                <div className="text-center py-8">
                                    <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="text-gray-500 dark:text-gray-400 mt-2">No assigned tasks found.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6">
                                    {assignedTasks.map((task) => {
                                        const userAssignment = task.assignedUsers?.find(assignment => assignment.user === user?.id);
                                        return (
                                            <div key={task._id} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            {task.title}
                                                        </h3>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                            {task.company?.companyName || 'Company'}
                                                        </p>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                                            {task.description}
                                                        </p>
                                                        <div className="flex items-center space-x-4 mt-3">
                                                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                                <MapPinIcon className="h-4 w-4 mr-1" />
                                                                {task.regions && task.regions.length > 0 ? task.regions.join(', ') : 'No regions'}
                                                            </div>
                                                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                                <CalendarIcon className="h-4 w-4 mr-1" />
                                                                {formatDate(task.startDate)} - {formatDate(task.endDate)}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2 mt-3">
                                                            {task.requiredSkills && task.requiredSkills.length > 0 ? (
                                                                task.requiredSkills.map((skill) => (
                                                                    <span key={skill._id} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs">
                                                                        {skill.name}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full text-xs">
                                                                    No skills specified
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end space-y-2">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                                            {task.status}
                                                        </span>
                                                        {userAssignment && userAssignment.designation && (
                                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                                {userAssignment.designation.replace('_', ' ')}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => navigate(`/dashboard/tasks/${task._id}`)}
                                                            className="inline-flex items-center px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-1" />
                                                            View Details
                                                        </button>
                                                    </div>
                                                </div>
                                                {userAssignment?.myReferralLink && userAssignment.designation === 'team_executive' && (
                                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                        <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                                            <div className="flex-1 min-w-0">
                                                                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                                                    Your Referral Link
                                                                </label>
                                                                <div className="flex items-center space-x-2">
                                                                    <LinkIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                                    <p className="text-xs text-blue-700 dark:text-blue-300 truncate font-mono">
                                                                        {userAssignment.myReferralLink}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => copyToClipboard(userAssignment.myReferralLink!, `ref-${task._id}`)}
                                                                className="ml-2 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs flex-shrink-0"
                                                            >
                                                                {copiedLink === `ref-${task._id}` ? (
                                                                    <>
                                                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                        Copied!
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <ClipboardIcon className="h-4 w-4 mr-1" />
                                                                        Copy
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    ) : activeTab === 'applications' ? (
                        <>
                            {myApplications.length === 0 ? (
                                <div className="text-center py-8">
                                    <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="text-gray-500 dark:text-gray-400 mt-2">No applications found.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6">
                                    {myApplications.filter(application => application.task).map((application) => (
                                        <div key={application._id} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                        {application.task?.title || 'Task Deleted'}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {application.task?.company?.companyName || 'Company'}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {application.task?.description || 'No description available'}
                                                    </p>
                                                    <div className="flex items-center space-x-4 mt-2">
                                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                            <MapPinIcon className="h-4 w-4 mr-1" />
                                                            {application.task?.regions && application.task.regions.length > 0 ? application.task.regions.join(', ') : 'No regions'}
                                                        </div>
                                                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                            <CalendarIcon className="h-4 w-4 mr-1" />
                                                            {application.task?.startDate ? formatDate(application.task.startDate) : 'N/A'} - {application.task?.endDate ? formatDate(application.task.endDate) : 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end space-y-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                                        {application.status}
                                                    </span>
                                                    {application.assignedDesignation && (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                            {application.assignedDesignation?.replace('_', ' ') || application.assignedDesignation}
                                                        </span>
                                                    )}
                                                    {application.status === 'approved' && application.task && (
                                                        <button
                                                            onClick={() => navigate(`/dashboard/tasks/${application.task._id}`)}
                                                            className="inline-flex items-center px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-1" />
                                                            View Details
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Applied On</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {formatDate(application.appliedAt)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Review Status</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {application.reviewedAt ? `Reviewed ${formatDate(application.reviewedAt)}` : 'Pending review'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Application Status</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                        {application.status}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Required Skills</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {application.task?.requiredSkills && application.task.requiredSkills.length > 0 ? (
                                                        application.task.requiredSkills.map((skill) => (
                                                            <span key={skill._id} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                                                                {skill.name}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                                                            No skills specified
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {application.applicationMessage && (
                                                <div className="mt-4">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Application Message</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                                                        {application.applicationMessage}
                                                    </p>
                                                </div>
                                            )}

                                            {application.rejectionReason && (
                                                <div className="mt-4">
                                                    <p className="text-sm text-red-500 dark:text-red-400 mb-2">Rejection Reason</p>
                                                    <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                                        {application.rejectionReason}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="mt-4 flex justify-between items-center">
                                                <div className="flex items-center space-x-4">
                                                    {application.status === 'approved' && application.assignedDesignation && (
                                                        <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                                                            ✓ Assigned as {application.assignedDesignation.replace('_', ' ')}
                                                        </span>
                                                    )}
                                                    {application.status === 'rejected' && (
                                                        <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                                                            ✗ Application rejected
                                                        </span>
                                                    )}
                                                    {application.status === 'applied' && (
                                                        <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                                                            ⏳ Under review
                                                        </span>
                                                    )}
                                                    {application.status === 'invited' && (
                                                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                                            📧 You've been invited to apply
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {application.status === 'approved' && (
                                                        <button
                                                            onClick={() => navigate(`/dashboard/tasks/${application.task._id}`)}
                                                            className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-2" />
                                                            View Details
                                                        </button>
                                                    )}
                                                    {application.status === 'invited' && (
                                                        <button
                                                            onClick={() => openInvitedApplicationForm(application.task)}
                                                            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                        >
                                                            <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                                                            Apply Now
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        // Available Tasks Tab
                        <>
                            {availableTasks.length === 0 ? (
                                <div className="text-center py-8">
                                    <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="text-gray-500 dark:text-gray-400 mt-2">No available tasks found.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6">
                                    {availableTasks.map((task) => (
                                        <div key={task._id} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                        {task.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                        {task.company?.companyName || 'Company'}
                                                    </p>
                                                    <p className="text-gray-700 dark:text-gray-300 mt-2 line-clamp-2">
                                                        {task.description || 'No description available'}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col items-end space-y-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                                        {task.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Duration</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {formatDate(task.startDate || task.createdAt)} - {formatDate(task.endDate || task.createdAt)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Required Skills</p>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {task.requiredSkills?.slice(0, 2).map((skill) => (
                                                            <span key={skill._id} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                                                                {skill.name}
                                                            </span>
                                                        ))}
                                                        {task.requiredSkills && task.requiredSkills.length > 2 && (
                                                            <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                                                                +{task.requiredSkills.length - 2} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4 flex justify-between items-center">
                                                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="flex items-center">
                                                        <MapPinIcon className="h-4 w-4 mr-1" />
                                                        {task.regions?.join(', ') || 'No regions'}
                                                    </span>
                                                    <span className="flex items-center">
                                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                                        {formatDate(task.createdAt)}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => openApplicationForm(task)}
                                                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                                >
                                                    Apply Now
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Application Form Modal */}
            {showApplicationForm && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Apply to Task
                            </h2>
                            <button
                                onClick={closeApplicationForm}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Task Information */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {selectedTask.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {selectedTask.company?.companyName}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Duration: {formatDate(selectedTask.startDate || selectedTask.createdAt)} - {formatDate(selectedTask.endDate || selectedTask.createdAt)}
                                </p>
                            </div>

                            {/* Application Message */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Application Message (Optional)
                                </label>
                                <textarea
                                    value={applicationMessage}
                                    onChange={(e) => setApplicationMessage(e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="Tell us why you're interested in this task..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={closeApplicationForm}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyToTask}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Submit Application
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invited Application Form Modal */}
            {showInvitedApplicationForm && selectedInvitedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Apply to Invited Task
                            </h2>
                            <button
                                onClick={() => {
                                    setShowInvitedApplicationForm(false);
                                    setSelectedInvitedTask(null);
                                    setInvitedApplicationMessage('');
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Task Information */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {selectedInvitedTask.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {selectedInvitedTask.company?.companyName}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Duration: {formatDate(selectedInvitedTask.startDate)} - {formatDate(selectedInvitedTask.endDate)}
                                </p>
                            </div>

                            {/* Application Message */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Application Message (Optional)
                                </label>
                                <textarea
                                    value={invitedApplicationMessage}
                                    onChange={(e) => setInvitedApplicationMessage(e.target.value)}
                                    placeholder="Tell us why you're interested in this task and how you can contribute..."
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    This message will be sent to the task administrator along with your application.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => {
                                    setShowInvitedApplicationForm(false);
                                    setSelectedInvitedTask(null);
                                    setInvitedApplicationMessage('');
                                }}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyToInvitedTask}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Submit Application
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Application Details Modal */}
            {showModal && selectedApplication && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Application Details
                            </h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Task Information */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                    Task Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Task Title</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedApplication.task.title}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Company</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedApplication.task.company?.companyName || 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Regions</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedApplication.task.regions?.join(', ') || 'No regions'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">States</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedApplication.task.states?.join(', ') || 'No states'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Application Status */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Application Status</p>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedApplication.status)}`}>
                                        {selectedApplication.status.toUpperCase()}
                                    </span>
                                </div>
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Applied On</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                        {formatDate(selectedApplication.appliedAt)}
                                    </p>
                                </div>
                            </div>


                            {/* Application Message */}
                            {selectedApplication.applicationMessage && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            Your Application Message
                                        </h3>
                                    </div>
                                    <div className="p-4">
                                        <p className="text-gray-700 dark:text-gray-300">
                                            {selectedApplication.applicationMessage}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Rejection Reason */}
                            {selectedApplication.rejectionReason && (
                                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                    <div className="p-4 border-b border-red-200 dark:border-red-800">
                                        <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                                            Rejection Reason
                                        </h3>
                                    </div>
                                    <div className="p-4">
                                        <p className="text-red-700 dark:text-red-200">
                                            {selectedApplication.rejectionReason}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskManagement;